/**
 * Pages publish their chat context through this hook.
 *
 * The DATA part (everything except handlers) goes to the shell only when its
 * JSON key changes, so an unchanged context does not churn the chat panel.
 * The HANDLERS part is refreshed after EVERY render regardless: some close
 * over changing state (DrugDetailPage's citation handler captures
 * `searchParams`), and a handler frozen at the first publish would act on
 * stale state with nothing on screen to say so.
 */
import { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { ShellContext } from './shellContext';
import { useTab } from './TabContext';
import type { PublishedContext, PublishedHandlers } from './types';

export function usePublishChatContext(pub: PublishedContext | null): void {
  const shell = useContext(ShellContext);
  const { tabId } = useTab();
  const setPageContext = shell?.setPageContext;
  const setPageHandlers = shell?.setPageHandlers;

  let data: Omit<PublishedContext, 'handlers'> | null = null;
  if (pub) {
    const { handlers: _handlers, ...rest } = pub;
    data = rest;
  }
  const key = JSON.stringify(data);
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    setPageContext?.(tabId, dataRef.current);
  }, [setPageContext, tabId, key]);

  // No deps on purpose: see the header.
  const ownHandlers = useRef<PublishedHandlers | undefined>(undefined);
  useLayoutEffect(() => {
    ownHandlers.current = pub?.handlers;
    setPageHandlers?.(tabId, pub?.handlers);
  });
  // Clear only what THIS instance set: in-tab navigation mounts the next page's
  // handlers in the same commit, and they must survive this page's unmount.
  useLayoutEffect(
    () => () => {
      if (ownHandlers.current) setPageHandlers?.(tabId, undefined, ownHandlers.current);
    },
    [setPageHandlers, tabId],
  );

  useEffect(
    () => () => {
      setPageContext?.(tabId, null);
    },
    [setPageContext, tabId],
  );
}
